import React, { FC, useEffect } from 'react';
import dynamic from 'next/dynamic';
import {
    withOptimizely,
    WithOptimizelyProps,
    selectForcedFeatures,
    getOptimizelyFeatureFlag
} from 'shared/optimizely';
import { useSelector } from 'react-redux';
import { AuthModalProps } from './AuthModal.types';
import { useLinkWithParam } from 'register/components/RegisterPage/utils/useLinkWithParam';

import { Tabs, Tab } from 'shared/components/Tabs/Tabs';
import RegisterSection from './section/Register';
const SignInSection = dynamic(() => import('./section/SignIn'), { ssr: false });

import registrationAndSignInTranslations from 'public/locales/en/registration-and-sign-in.json';
import { getTranslator } from 'shared/i18n/getTranslator';
import { StyledModal } from './AuthModal.styles';
import triggerTracking from '@blueprint/tracking/dist/Tracking';
const t = getTranslator(registrationAndSignInTranslations);
const signInTitleTn = t('signIn.title');
const registerTitleTn = t('detailsSection.title');
const headingTn = t('heading');
const signInModalHeadingTn = t('defaultHeading');

export const AuthModal: FC<AuthModalProps & WithOptimizelyProps> = (props) => {
    const featureFlagOverrides = useSelector(selectForcedFeatures);
    const isFeatureEnabled = getOptimizelyFeatureFlag(
        'sign_in_modal',
        featureFlagOverrides,
        props.optimizely
    );

    const {
        heading,
        isShowing,
        close,
        actionAfterAuth,
        resetActionAfterAuth = () => null,
        pathForSignIn,
        isSignInModalEnabled = isFeatureEnabled || false
    } = props;

    // Todo: Remove this tracking once the Sign in modal has gone to 100%
    useEffect(() => {
        triggerTracking({
            event: 'GAEvent',
            eventCategory: 'optimizely',
            eventAction: 'crme_768_sign_in_modal_v1',
            eventLabel: isFeatureEnabled ? 'variation' : 'control'
        });
    }, []);

    // This runs when the user clicks the close button, outside the modal or uses the Escape key (not on submit)
    const toggleClosed = () => {
        triggerTracking({
            event: 'GAEvent',
            eventCategory: 'registration',
            eventAction: 'form closed',
            eventLabel: actionAfterAuth?.eventLabel
        });
        resetActionAfterAuth();
        close();
    };

    useEffect(() => {
        if (isShowing) {
            triggerTracking({
                event: 'GAEvent',
                eventCategory: 'registration',
                eventAction: 'form shown',
                eventLabel: actionAfterAuth?.eventLabel
            });
        }
    }, [isShowing]);

    return (
        <StyledModal
            isShowing={isShowing}
            close={toggleClosed}
            modalTitle={
                isSignInModalEnabled
                    ? heading || signInModalHeadingTn
                    : undefined
            }
            isSignInModalEnabled={isSignInModalEnabled}
        >
            {isSignInModalEnabled ? (
                <Tabs
                    ariaLabel="auth-tab"
                    isFullWidth
                    handleClick={(tabIndex) =>
                        triggerTracking({
                            event: 'GAEvent',
                            eventCategory: 'registration',
                            eventAction: tabIndex
                                ? 'click_to_signin'
                                : 'click_to_register',
                            eventLabel: actionAfterAuth?.eventLabel
                        })
                    }
                >
                    <Tab label={registerTitleTn}>
                        <RegisterSection
                            {...props}
                            pathForSignIn={useLinkWithParam(pathForSignIn)}
                            heading={heading || headingTn}
                            isShowing={isShowing}
                            close={close}
                            actionAfterAuth={actionAfterAuth}
                            resetActionAfterAuth={resetActionAfterAuth}
                            isSignInModalEnabled={isSignInModalEnabled}
                        />
                    </Tab>
                    <Tab label={signInTitleTn}>
                        <SignInSection
                            {...props}
                            isShowing={isShowing}
                            close={close}
                            actionAfterAuth={actionAfterAuth}
                            resetActionAfterAuth={resetActionAfterAuth}
                            isSignInModalEnabled={isSignInModalEnabled}
                        />
                    </Tab>
                </Tabs>
            ) : (
                <RegisterSection
                    {...props}
                    pathForSignIn={useLinkWithParam(pathForSignIn)}
                    heading={heading || headingTn}
                    isShowing={isShowing}
                    close={close}
                    actionAfterAuth={actionAfterAuth}
                    resetActionAfterAuth={resetActionAfterAuth}
                    isSignInModalEnabled={isSignInModalEnabled}
                />
            )}
        </StyledModal>
    );
};

export default withOptimizely(AuthModal);
